/**
 * 🔍 RootCauseAnalysisPanel — MASP: Análise de Causa Raiz
 *
 * Panel for structured root cause analysis with:
 * - Problem statement
 * - Root cause description + category (6M)
 * - 5 Whys tool
 * - Ishikawa diagram data entry
 */

import React, { useState, useCallback, useMemo } from 'react';
import { modalInputClass, modalSelectClass } from '../layout/EnterpriseModal';
import { ROOT_CAUSE_CATEGORY_LABELS } from '../../types/issue';
import type { RootCauseAnalysis, RootCauseCategory, FiveWhyEntry, IshikawaCause, IssueStatus } from '../../types/issue';
import { Save, GitBranch, Layers } from 'lucide-react';

interface RootCauseAnalysisPanelProps {
  value: RootCauseAnalysis | null;
  onChange: (analysis: RootCauseAnalysis) => void;
  onSave: (analysis: RootCauseAnalysis) => void;
  saving?: boolean;
  readOnly?: boolean;
  issueStatus: IssueStatus;
}

const CATEGORIES: RootCauseCategory[] = ['method', 'machine', 'material', 'manpower', 'environment', 'measurement'];

const CATEGORY_ICON_BG: Record<RootCauseCategory, string> = {
  method: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  machine: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  material: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  manpower: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  environment: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  measurement: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

function emptyAnalysis(): RootCauseAnalysis {
  return {
    problem_statement: '',
    root_cause_description: '',
    root_cause_category: 'method',
    five_whys: [
      { level: 1, question: 'Por que o problema ocorreu?', answer: '' },
    ],
    ishikawa: CATEGORIES.map(cat => ({ category: cat, causes: [] })),
  };
}

export const RootCauseAnalysisPanel: React.FC<RootCauseAnalysisPanelProps> = ({
  value,
  onChange,
  onSave,
  saving = false,
  readOnly = false,
  issueStatus,
}) => {
  const analysis = value ?? emptyAnalysis();
  
  // Predict which tab should be active by default if there's existing data
  const defaultTab = useMemo(() => {
    if (analysis.ishikawa?.some(cat => cat.causes.length > 0)) return 'ishikawa';
    // Se "Por que o problema ocorreu?" for respondido, considera-se 5 porquês preenchido
    if (analysis.five_whys?.length && analysis.five_whys[0].answer.trim() !== '') return 'five_whys';
    return 'none';
  }, [analysis.ishikawa, analysis.five_whys]);

  const [activeTab, setActiveTab] = useState<'none' | 'five_whys' | 'ishikawa'>(defaultTab);

  // Lógica Lean / Rito MASP:
  const isStep1 = issueStatus === 'open' || issueStatus === 'investigation';
  const isStep3 = issueStatus === 'root_cause';
  const isFinalStep = !isStep1 && !isStep3;

  // Mostra as ferramentas se estiver no step 3 (Causa Raiz) ou se tivermos passado dessa fase (Step 4+)
  const showSupportTools = !isStep1;
  const showConsolidatedCause = !isStep1;
  const isProblemStatementReadOnly = readOnly || !isStep1;
  
  // Se está readOnly (Step 4+), a seleção da ferramenta também fica congelada para não confundir o usuário
  const isToolSelectionReadOnly = readOnly || isFinalStep;
  const isAnalysisReadOnly = readOnly || isFinalStep;

  const update = useCallback((patch: Partial<RootCauseAnalysis>) => {
    onChange({ ...analysis, ...patch });
  }, [analysis, onChange]);

  // ── 5 Whys Handlers ───────────────────────────────
  const whys = analysis.five_whys ?? [];

  const updateWhy = useCallback((index: number, field: 'question' | 'answer', val: string) => {
    const updated = [...whys];
    updated[index] = { ...updated[index], [field]: val };
    update({ five_whys: updated });
  }, [whys, update]);

  const addWhy = useCallback(() => {
    if (whys.length >= 5) return;
    const nextLevel = whys.length + 1;
    update({
      five_whys: [...whys, { level: nextLevel, question: `Por que? (nível ${nextLevel})`, answer: '' }],
    });
  }, [whys, update]);

  const removeWhy = useCallback((index: number) => {
    update({ five_whys: whys.filter((_, i) => i !== index) });
  }, [whys, update]);

  // ── Ishikawa Handlers ─────────────────────────────
  const ishikawa = analysis.ishikawa ?? CATEGORIES.map(cat => ({ category: cat, causes: [] }));

  const addIshikawaCause = useCallback((catIndex: number, cause: string) => {
    if (!cause.trim()) return;
    const updated = [...ishikawa];
    updated[catIndex] = { ...updated[catIndex], causes: [...updated[catIndex].causes, cause.trim()] };
    update({ ishikawa: updated });
  }, [ishikawa, update]);

  const removeIshikawaCause = useCallback((catIndex: number, causeIndex: number) => {
    const updated = [...ishikawa];
    updated[catIndex] = { ...updated[catIndex], causes: updated[catIndex].causes.filter((_, i) => i !== causeIndex) };
    update({ ishikawa: updated });
  }, [ishikawa, update]);

  return (
    <div className="space-y-6 pt-3">
      {/* ── Problem Statement ──────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            DECLARAÇÃO DO DESVIO *
          </label>
          {!isAnalysisReadOnly && (
            <button
              type="button"
              onClick={() => onSave(analysis)}
              disabled={saving}
              className="flex items-center gap-1.5 rounded bg-blue-600 px-3.5 py-1.5 text-[11px] font-bold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Save size={13} />
              {saving ? 'Salvando...' : 'Salvar Análise'}
            </button>
          )}
        </div>
        <textarea
          value={analysis.problem_statement}
          onChange={e => update({ problem_statement: e.target.value })}
          placeholder="Descreva o problema de forma clara e objetiva..."
          className={`${modalInputClass} min-h-[60px] resize-y`}
          rows={2}
          readOnly={isProblemStatementReadOnly}
        />
      </div>

      {/* ── Support Tool Selection (Workplace) ─────────── */}
      {showSupportTools && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              MÉTODO DE ANÁLISE {isToolSelectionReadOnly && <span className="opacity-50">(Bloqueado para edição)</span>}
            </label>
            <div className="flex bg-slate-900/50 p-1 rounded-md w-max">
              {([
                { key: 'none', label: 'Nenhum' },
                { key: 'five_whys', label: '5 Porquês' },
                { key: 'ishikawa', label: 'Ishikawa' },
              ] as const).map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  disabled={isToolSelectionReadOnly && activeTab !== tab.key}
                  onClick={() => !isAnalysisReadOnly && !isToolSelectionReadOnly && setActiveTab(tab.key)}
                  className={`px-3 py-1 text-[11px] font-bold rounded transition-all ${
                    activeTab === tab.key
                      ? 'bg-slate-700 text-blue-400 shadow-sm'
                      : 'text-slate-500 hover:text-slate-300 disabled:opacity-30'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

        {/* ── 5 Whys Embedded ───────────────────────────── */}
        {activeTab === 'five_whys' && (
          <div className="space-y-3 pt-2">
            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
              Pergunte &quot;Por quê?&quot; repetidamente até encontrar a causa raiz (máximo 5 níveis).
            </p>
            <div className="space-y-2">
            {whys.map((why, idx) => (
              <div key={idx} className="relative rounded border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
                    {why.level}
                  </span>
                  <input
                    value={why.question}
                    onChange={e => updateWhy(idx, 'question', e.target.value)}
                    className="flex-1 bg-transparent text-[11px] font-bold text-slate-800 outline-none dark:text-slate-200 placeholder:font-normal"
                    placeholder="Por quê?"
                    readOnly={isAnalysisReadOnly}
                  />
                  {!isAnalysisReadOnly && idx > 0 && (
                    <button type="button" onClick={() => removeWhy(idx)} className="text-[10px] uppercase font-bold text-red-500 hover:text-red-700">
                      Remover
                    </button>
                  )}
                </div>
                <textarea
                  value={why.answer}
                  onChange={e => updateWhy(idx, 'answer', e.target.value)}
                  placeholder="Resposta..."
                  className={`${modalInputClass} min-h-[48px] resize-y text-[11px]`}
                  rows={2}
                  readOnly={isAnalysisReadOnly}
                />
              </div>
            ))}
            </div>
            {!isAnalysisReadOnly && whys.length < 5 && (
              <button
                type="button"
                onClick={addWhy}
                className="w-full rounded border border-dashed border-slate-300 py-2.5 text-[11px] font-bold text-slate-500 transition hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                + Adicionar Porquê (Nível {whys.length + 1})
              </button>
            )}
          </div>
        )}

        {/* ── Ishikawa Embedded ─────────────────────────── */}
        {activeTab === 'ishikawa' && (
          <div className="space-y-4 pt-2">
            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
              Diagrama de Ishikawa (Espinha de Peixe) — Mapeie causas por categoria (6M).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {ishikawa.map((group, catIdx) => (
                <IshikawaCategory
                  key={group.category}
                  category={group.category}
                  causes={group.causes}
                  onAdd={cause => addIshikawaCause(catIdx, cause)}
                  onRemove={causeIdx => removeIshikawaCause(catIdx, causeIdx)}
                  readOnly={isAnalysisReadOnly}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* ── Consolidated Cause ─────────────────────────── */}
      {showConsolidatedCause && (
      <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-800">
        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          CAUSA RAIZ DEFINIDA *
        </label>
        <div className="flex flex-col gap-4">
          <div className="flex-1 group">
            <textarea
              value={analysis.root_cause_description}
              onChange={e => update({ root_cause_description: e.target.value })}
              placeholder="Descreva a causa raiz consolidada (conclusão das análises acima)..."
              className={`${modalInputClass} min-h-[80px] resize-y bg-emerald-50/20 border-emerald-200/50 hover:border-emerald-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 dark:bg-emerald-900/5 dark:border-emerald-900/30 dark:focus:border-emerald-500/50 transition-all`}
              rows={3}
              readOnly={isAnalysisReadOnly}
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              CATEGORIA PRINCIPAL
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => !isAnalysisReadOnly && update({ root_cause_category: cat })}
                  className={`rounded-sm px-3 py-1.5 text-[10px] font-bold transition-all ${
                    analysis.root_cause_category === cat
                      ? CATEGORY_ICON_BG[cat] + ' ring-2 ring-offset-1 ring-current shadow-sm'
                      : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {ROOT_CAUSE_CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

/** Single Ishikawa category card */
function IshikawaCategory({
  category,
  causes,
  onAdd,
  onRemove,
  readOnly,
}: {
  category: RootCauseCategory;
  causes: string[];
  onAdd: (cause: string) => void;
  onRemove: (index: number) => void;
  readOnly?: boolean;
}) {
  const [newCause, setNewCause] = useState('');

  const handleAdd = () => {
    if (newCause.trim()) {
      onAdd(newCause);
      setNewCause('');
    }
  };

  return (
    <div className="rounded border border-slate-100 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-800 flex flex-col overflow-hidden">
      <div className={`px-3 py-2 ${CATEGORY_ICON_BG[category]}`}>
        <h4 className="text-[10px] font-bold uppercase tracking-wider">
          {ROOT_CAUSE_CATEGORY_LABELS[category]}
        </h4>
      </div>
      <div className="space-y-1.5 p-2 flex-1">
        {causes.map((cause, idx) => (
          <div key={idx} className="flex items-start gap-1.5 rounded-md bg-slate-50 px-2 py-1.5 dark:bg-slate-900/40">
            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
            <span className="flex-1 text-[11px] font-medium text-slate-700 dark:text-slate-300 leading-tight block">{cause}</span>
            {!readOnly && (
              <button type="button" onClick={() => onRemove(idx)} className="shrink-0 text-[10px] font-bold text-slate-300 hover:text-red-500 transition-colors">
                ✕
              </button>
            )}
          </div>
        ))}
        {causes.length === 0 && (
          <p className="py-2 text-center text-[10px] font-medium text-slate-400 dark:text-slate-500">Nenhuma causa</p>
        )}
      </div>
      {!readOnly && (
        <div className="p-2 pt-0 mt-auto">
          <div className="flex items-center gap-1.5">
            <input
              value={newCause}
              onChange={e => setNewCause(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="Nova causa..."
              className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-[10px] font-medium outline-none focus:border-blue-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={!newCause.trim()}
              className="rounded-md bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              +
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RootCauseAnalysisPanel;
